import React, { useState, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import { QuestionMarkCircleIcon, ExclamationIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/solid';
import FormActionButtons from './FormCurrentQuestionActionButtons';
import DateOfBirthPicker from './DateOfBirthPicker';
import { isSubquestionVisible, questionHasConditionalSubquestions, deriveQuestionValidation, isRequiredOtherDetailMissing } from '../../../../utils/formVisibility';
import { resizeTextarea, createTextareaRefCallback } from '../../../../utils/textareaResize';
import { otherOptionFieldKey } from '../utils/sanitizeOptionForKey';

// Keyboard handler for option tiles — lets users activate options with
// Enter or Space, matching the behaviour of native radio/checkbox inputs.
function handleOptionKeyDown(e, clickHandler) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    clickHandler();
  }
}

// Function to convert text with URLs to clickable links
const formatTextWithLinks = (text) => {
  if (!text) return '';
  
  // Replace escaped newlines with actual newlines
  text = text.replace(/\\n/g, '\n');
  
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  // Find all matches first
  const matches = text.match(urlRegex) || [];
  if (matches.length === 0) return text;
  
  // Use indices to split text properly
  const result = [];
  let lastIndex = 0;
  
  matches.forEach((match, index) => {
    // Find the position of this URL
    const matchIndex = text.indexOf(match, lastIndex);
    
    // Add the text before the URL
    if (matchIndex > lastIndex) {
      result.push(<span key={`text-${index}`}>{text.substring(lastIndex, matchIndex)}</span>);
    }
    
    // Add the URL as a link
    result.push(
      <a 
        key={`link-${index}`}
        href={match} 
        target="_blank" 
        rel="noopener noreferrer"
        className="text-violet-700 hover:text-violet-900 underline"
      >
        {match}
      </a>
    );
    
    // Update the last index
    lastIndex = matchIndex + match.length;
  });
  
  // Add any remaining text after the last URL
  if (lastIndex < text.length) {
    result.push(<span key="text-last">{text.substring(lastIndex)}</span>);
  }
  
  return result;
};

export default function FormCurrentQuestionPanel({
  handleNextQuestion,
  handleCancelEdit,
  handleSubmit,
  currentPanel,
  currentQuestion,
  formState,
  register,
  control,
  getValues,
  setValue,
  setFormState,
  totalQuestions,
  currentQuestionNumber,
  formKey,
  userOrganisation,
  questionHeadingRef,
  preButtonContent,
  isSaving = false,
  storedAnswerLookup = {},
  // Flat answer lookups for SIBLING forms, keyed by formKey, so a subquestion
  // whose conditionsToShow uses `conditionType: 'otherForm'` (e.g. the CYP
  // periods questions gated on the About form's sex assigned at birth) can be
  // evaluated here. Forms without cross-form conditions never pass this.
  externalLookups = {},
}) {

  // Determine if current question has prefill data  
  const hasPrefillData = React.useMemo(() => {
    if (!currentQuestion?.subquestions) return false;
    
    // Check if any subquestions have prefilled answers that haven't been submitted yet
    return Object.values(currentQuestion.subquestions).some(sq => 
      sq.answer !== undefined && 
      sq.answer !== null && 
      sq.answer !== "No answer given" &&
      !currentQuestion.hasSeenQuestion
    );
  }, [currentQuestion]);
  
  // Whether this form requires every subquestion to be answered (scales with many
  // items where accidentally skipping one is a common mistake).
  const requiresAllSubquestions = formKey === 'bdefs' || formKey === 'weiss' || formKey === 'mdq' || formKey === 'cnst' || formKey === 'spat';

  // Whether the current question has any subquestion with conditionsToShow.
  // Conditional questions (alcohol, smoking) need every-visible-answered semantics,
  // while non-conditional questions (name, weight) use has-any-value so genuinely
  // optional fields don't block saving.
  const hasConditionalSqs = useMemo(() => {
    return questionHasConditionalSubquestions(currentQuestion);
  }, [currentQuestion]);

  // ── Scoped field watching ───────────────────────────────────────────────
  // We compute the specific React Hook Form field keys for the current
  // question's subquestions and use them in two complementary ways below.

  const watchedKeys = useMemo(() => {
    if (!currentQuestion?.subquestions) return [];
    const keys = Object.keys(currentQuestion.subquestions).map(
      (sqIdx) => `question_${formState.currentPanel}_${formState.currentQuestion}_${sqIdx}`
    );
    // A required detail box has to be watched as well as the tick, or
    // typing in it would not re-enable Continue. Optional boxes are left
    // out: they never decide whether the question is answered.
    Object.entries(currentQuestion.subquestions).forEach(([sqIdx, sq]) => {
      const required = Array.isArray(sq.requiredOtherOptions) ? sq.requiredOtherOptions : [];
      const answerKey = `question_${formState.currentPanel}_${formState.currentQuestion}_${sqIdx}`;
      required.forEach((option) => {
        keys.push(otherOptionFieldKey(answerKey, option));
      });
    });
    return keys;
  }, [currentQuestion, formState.currentPanel, formState.currentQuestion]);

  // useWatch is used ONLY as a child-scoped re-render trigger: it re-renders
  // THIS component (not the parent) whenever one of the current question's
  // fields changes, which is what keeps typing/selection responsive without a
  // full-form re-render cascade.
  //
  // We deliberately do NOT read the displayed values from useWatch's return.
  // useWatch computes its value once at mount (React.useState initialiser) and
  // afterwards only updates via a subscription gated by the field name it was
  // tracking at emit time. When edit mode calls setValue() for the target
  // question BEFORE the question-position re-render, those emits are filtered
  // out and the snapshot goes stale, so previously entered answers render as
  // unselected until an unrelated change forces a fresh read. Reading from
  // getValues (which always reflects control._formValues synchronously) avoids
  // that staleness entirely and is robust to any prefill/navigation ordering.
  //
  // IMPORTANT: do not "simplify" this back to reading watchSignal directly --
  // that reintroduces the edit-mode prefill display regression.
  const watchSignal = useWatch({ control, name: watchedKeys });

  // Authoritative current values for the current question's subquestions.
  // getValues(namesArray) returns an index-aligned array (even for a single
  // key), reading each key from the live store. watchSignal is the dependency
  // that re-runs this read whenever a watched field changes.
  const watchedValues = useMemo(
    () => (watchedKeys.length ? getValues(watchedKeys) : []),
    [watchedKeys, watchSignal, getValues]
  );

  // O(1) lookup map so the render body can read values by answer key without
  // calling watch() inline.
  const valuesByKey = useMemo(() => {
    const map = {};
    watchedKeys.forEach((key, i) => {
      map[key] = watchedValues[i];
    });
    return map;
  }, [watchedKeys, watchedValues]);

  // ── Consolidated validation derivation ─────────────────────────────────
  // All visibility, answered-check, and warning-banner logic is computed in
  // a single pass by the pure deriveQuestionValidation utility.  This
  // replaces three separate useMemo hooks and a useEffect+subscription that
  // previously duplicated the same visibility lookup logic.
  const {
    liveAnswerLookup,
    showWarningBanner,
    hiddenHaveAnswers,
    hasAnyValue,
    allVisibleAnswered,
    completeValidation,
  } = useMemo(
    () => deriveQuestionValidation(currentQuestion, watchedKeys, watchedValues, requiresAllSubquestions, storedAnswerLookup, externalLookups),
    [currentQuestion, watchedKeys, watchedValues, requiresAllSubquestions, storedAnswerLookup, externalLookups]
  );

  // "Other" (or any option named in requiredOtherOptions) is not an answer
  // until the parent types what they mean. Every other detail box stays
  // optional, including Yes boxes that only offer extra context.
  const requiredDetailMissing = useMemo(() => {
    if (!currentQuestion?.subquestions) return false;
    return Object.entries(currentQuestion.subquestions).some(([sqIdx, sq]) => {
      const answerKey = `question_${formState.currentPanel}_${formState.currentQuestion}_${sqIdx}`;
      const detailByOption = {};
      const required = Array.isArray(sq.requiredOtherOptions) ? sq.requiredOtherOptions : [];
      required.forEach((option) => {
        detailByOption[option] = valuesByKey[otherOptionFieldKey(answerKey, option)];
      });
      return isRequiredOtherDetailMissing(sq, valuesByKey[answerKey], detailByOption);
    });
  }, [currentQuestion, formState.currentPanel, formState.currentQuestion, valuesByKey]);


  return (
    <>
      {/* Guard: when PatientFormTemplate is realigning to the first available subsection/question,
          currentPanel/currentQuestion can briefly be null. Avoid crashing and show a lightweight placeholder. */}
      {(!currentPanel || !currentQuestion) && (
        <div className="mb-4 text-left">
          <h2 className="text-2xl font-bold">Loading…</h2>
        </div>
      )}

      {currentPanel && currentQuestion && (
        <>
      {/* Subsection-title block (mobile + desktop). The stable id is the
          scroll target used by edit/navigation in every consumer so the user
          always lands on the subsection title (which also shows "Question X of
          Y"), not the question title below it. scroll-margin-top keeps the
          fixed top navbar from overlapping the heading after scrolling. */}
      <div id="form-section-heading" style={{ scrollMarginTop: '5rem' }}>
        {/* Mobile layout: Part/Question number above title */}
        <div className="md:hidden mb-4">
          {currentQuestionNumber && totalQuestions && (
            <div className="text-left mb-2">
              <span className="text-sm text-violet-800 font-medium">
                {(formKey === 'bdefs' || formKey === 'weiss' || formKey === 'mdq') ? 'Part' : 'Question'} {currentQuestionNumber} of {totalQuestions}
              </span>
            </div>
          )}
          <div className="border-b border-gray-200 pb-2">
            <h2 className="text-2xl font-bold">{currentPanel.formSubsectionTitle}</h2>
          </div>
        </div>

        {/* Desktop layout: Side by side */}
        <div className="hidden md:flex justify-between items-center mb-4 border-b border-gray-200 pb-2">
          <h2 className="text-2xl font-bold">{currentPanel.formSubsectionTitle}</h2>
          {currentQuestionNumber && totalQuestions && (
          <span className="text-sm text-violet-800 font-medium w-1/6 text-right">
            {(formKey === 'bdefs' || formKey === 'weiss') ? 'Part' : 'Question'} {currentQuestionNumber} of {totalQuestions}
          </span>
          )}
        </div>
      </div>

{/* Show prefill notice if applicable */}
{hasPrefillData && (
        <div className="mb-4 bg-blue-50 border-blue-200 border rounded-2xl p-4 text-left">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-blue-500" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <div className="text-sm font-semibold text-blue-700">
                {userOrganisation === 'ThinkDivergent' 
                  ? 'Some information has been pre-filled by ThinkDivergent. Please review and confirm or edit where applicable.'
                  : 'Some information has been pre-filled by your healthcare provider. Please review and confirm or edit where applicable.'
                }
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warning Message Banner */}
{currentQuestion.questionWarningMessage && (
  <div id="question-warning-banner" role="alert" className="mb-4 bg-amber-50 border-amber-200 border rounded-2xl p-4 text-left">
    <div className="flex items-start">
      <div className="flex-shrink-0">
        <ExclamationIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
      </div>
      <div className="ml-3">
        <div className="text-sm font-semibold text-amber-700 whitespace-pre-line">
          {formatTextWithLinks(currentQuestion.questionWarningMessage)}
        </div>
      </div>
    </div>
  </div>
)}

      {/* Info Message Banner */}
{currentQuestion.questionInfoMessage && (
  <div id="question-info-banner" className="mb-4 bg-blue-50 border-blue-200 border rounded-2xl p-4 text-left">
    <div className="flex items-start">
      <div className="flex-shrink-0">
        <InformationCircleIcon className="h-5 w-5 text-blue-500" aria-hidden="true" />
      </div>
      <div className="ml-3">
        <div className="text-sm font-semibold text-blue-700 whitespace-pre-line">
          {formatTextWithLinks(currentQuestion.questionInfoMessage)}
        </div>
      </div>
    </div>
  </div>
)}

<form
  onSubmit={handleSubmit((data) => handleNextQuestion(data, false))}
  aria-describedby={[
    currentQuestion.questionWarningMessage ? 'question-warning-banner' : null,
    currentQuestion.questionInfoMessage ? 'question-info-banner' : null,
  ].filter(Boolean).join(' ') || undefined}
>
 <h3
   ref={questionHeadingRef}
   tabIndex={-1}
   className="flex items-center text-lg font-bold mb-4 outline-none"
 >
          {currentQuestion.questionTitle}
          {currentQuestion.questionTooltip && (
            <>
              <QuestionMarkCircleIcon
                className="w-5 h-5 flex-shrink-0 ml-2 text-violet-700"
                data-tooltip-id={`tooltip-${formState.currentPanel}-${formState.currentQuestion}`}
                data-tooltip-content={currentQuestion.questionTooltip}
                aria-label="More information"
              />
              <Tooltip
                id={`tooltip-${formState.currentPanel}-${formState.currentQuestion}`}
                place="top"
                opacity={1}
                style={{
                  maxWidth: '300px',
                  whiteSpace: 'normal',
                  backgroundColor: '#111111',
                  borderRadius: '15px',
                  color: '#fff',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  lineHeight: 1.5,
                  padding: '12px 16px',
                  zIndex: 50,
                }}
              />
            </>
          )}
        </h3>

        {/* Render Questions */}
        {Object.entries(currentQuestion.subquestions).map(([subquestionIndex, subquestion], index) => {
          const answerKey = `question_${formState.currentPanel}_${formState.currentQuestion}_${subquestionIndex}`;
          const currentValue = valuesByKey[answerKey];

          // Evaluate visibility for this subquestion based on conditionsToShow rules.
          // Non-applicable subquestions are rendered greyed-out and disabled rather than hidden,
          // so the patient can still see what they answered and gets a clear "undo" window.
          const isVisible = isSubquestionVisible(subquestion, liveAnswerLookup, externalLookups);

          // Check if this subquestion has noneOptions
          const hasNoneOptions = subquestion.noneOptions && Array.isArray(subquestion.noneOptions) && subquestion.noneOptions.length > 0;

          // Anchor the warning banner at the first subquestion that has conditionsToShow,
          // regardless of current visibility state. This gives the banner a stable DOM
          // position so the max-height transition animates smoothly instead of popping.
          const isFirstConditionalSubquestion = subquestion.conditionsToShow &&
            Object.entries(currentQuestion.subquestions).findIndex(
              ([, sq]) => sq?.conditionsToShow
            ) === index;

          return (
            <div key={subquestionIndex}>
              {isFirstConditionalSubquestion && (
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showWarningBanner ? 'max-h-40 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
                  <div className="bg-amber-50 border-amber-200 border rounded-2xl p-4 text-left">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <ExclamationIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-semibold text-amber-700">
                          Some follow-up questions below are no longer applicable based on your answer above.
                        </div>
                        {hiddenHaveAnswers && (
                          <div className="text-sm font-semibold text-amber-700 mt-1">
                            When you proceed to the next question, your answers to the follow-up questions below will be cleared.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className={`flex flex-col md:flex-row md:items-start gap-2 md:gap-4 pb-4 transition-opacity duration-300 ease-in-out ${!isVisible ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}>
              {/* Left column: question label, plus optional hint stacked under it
                  so instructional text stays with the title rather than sitting
                  between the title and the input (the previous sibling layout
                  pushed SEQ "Tell us more" hints into the options column). */}
              <div className="w-full md:w-72">
              <span className="font-semibold text-lg md:text-base break-words flex items-start">
                {requiresAllSubquestions && (
                  <CheckCircleIcon 
                    className={`w-5 h-5 mr-2 flex-shrink-0 mt-1 ${
                      completeValidation.answeredSubquestions[subquestionIndex] 
                        ? 'text-green-500' 
                        : 'text-gray-300'
                    }`} 
                  />
                )}
                {subquestion.subquestionDisplayText}
              </span>
              {subquestion.subquestionHintText && (
                <span className="text-sm text-gray-500 block mt-1 whitespace-pre-line">
                  {subquestion.subquestionHintText}
                </span>
              )}
              </div>
              <div className="w-full md:w-96">
                {subquestion.inputType === "freeTextNumerical" ? (
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder={subquestion.placeholder || ""}
                    aria-label={subquestion.subquestionDisplayText}
                    className="w-full p-2 border rounded-md bg-white text-black"
                    {...register(answerKey)}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                    }}
                  />
                ) : subquestion.inputType === "date" ? (
                  <DateOfBirthPicker
                    value={currentValue || ''}
                    onChange={(date) => setValue(answerKey, date)}
                    placeholder={subquestion.placeholder || "Select date of birth"}
                    className="w-full"
                    // CYP forms define minAgeYears/maxAgeYears on the
                    // subquestion (e.g. 0-18 for a child's DOB). When
                    // absent the component falls back to its built-in
                    // adult defaults (18-120), so existing forms are
                    // unaffected.
                    minAgeYears={subquestion.minAgeYears}
                    maxAgeYears={subquestion.maxAgeYears}
                  />
                ) : subquestion.inputType === "selectOne" || subquestion.inputType === "selectMultiple" ? (
                  <ul
                    className="space-y-2"
                    role={subquestion.inputType === "selectOne" ? "radiogroup" : "group"}
                    aria-label={subquestion.subquestionDisplayText}
                  >
                    {/* Main options */}
                    {subquestion.options.map((option) => {
                      const isSelected = Array.isArray(currentValue)
                        ? currentValue?.includes(option)
                        : currentValue === option;

                      const hasOtherInput = subquestion.otherOptions && option in subquestion.otherOptions;
                      const shouldShowOtherInput = hasOtherInput && isSelected;
                      // A named option's box is a real field, with a visible
                      // label, because the parent cannot continue without it.
                      // Optional boxes keep the placeholder-only treatment.
                      const detailIsRequired = Array.isArray(subquestion.requiredOtherOptions)
                        && subquestion.requiredOtherOptions.includes(option);
                      const detailPrompt = subquestion.otherOptions?.[option] || "Please provide details";
                      const detailFieldName = otherOptionFieldKey(answerKey, option);

                      const selectHandler = () => {
                        if (subquestion.inputType === "selectOne") {
                          setValue(answerKey, currentValue === option ? null : option);
                          if (hasOtherInput && currentValue !== option) {
                            setFormState(prev => ({ ...prev, showOtherInput: true }));
                          } else if (hasOtherInput) {
                            setFormState(prev => ({ ...prev, showOtherInput: false }));
                          }
                        } else {
                          const currentArray = Array.isArray(currentValue)
                            ? currentValue
                            : currentValue ? [currentValue] : [];
                          let updatedValue = currentArray;
                          if (hasNoneOptions) {
                            updatedValue = updatedValue.filter(v =>
                              !subquestion.noneOptions.includes(v)
                            );
                          }
                          if (isSelected) {
                            updatedValue = updatedValue.filter(v => v !== option);
                          } else {
                            updatedValue = [...updatedValue, option];
                          }
                          setValue(answerKey, updatedValue);
                          const anySelectedHasOtherInput = updatedValue.some(
                            val => subquestion.otherOptions && val in subquestion.otherOptions
                          );
                          setFormState(prev => ({
                            ...prev,
                            showOtherInput: anySelectedHasOtherInput
                          }));
                        }
                      };

                      return (
                        <li
                          key={option}
                          role={subquestion.inputType === "selectOne" ? "radio" : "checkbox"}
                          aria-checked={isSelected}
                          tabIndex={0}
                          onClick={selectHandler}
                          onKeyDown={(e) => handleOptionKeyDown(e, selectHandler)}
                          className={`
                            flex flex-col p-2 
                            rounded-lg transition-all duration-300 
                            cursor-pointer group
                            ${isSelected
                              ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                              : 'bg-violet-100 hover:bg-violet-200'
                            }
                            `}
                        >
                          <div className="flex items-center gap-3">
                            <div aria-hidden="true" className={`
                              flex-shrink-0 w-4 h-4 rounded-full border-2
                              transition-all duration-200 ease-in-out
                              ${isSelected
                                ? 'border-violet-700 bg-violet-700'
                                : 'border-gray-300 group-hover:border-violet-400'
                              }
                              flex items-center justify-center
                              `}>
                              {isSelected && (
                                <div className={`
                                  ${subquestion.inputType === "selectMultiple"
                                    ? 'w-2 h-2 bg-white rounded'
                                    : 'w-1.5 h-1.5 bg-white rounded-full'
                                  }
                                  transition-all duration-200 ease-in-out`} />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className={`font-semibold transition-colors duration-200 ${isSelected ? 'text-violet-900' : 'text-gray-700'}`}>
                                {option}
                              </span>
                              {subquestion.suchAsText && subquestion.suchAsText[option] && (
                                <span className={`text-sm font-semibold italic transition-colors duration-200 ${isSelected ? 'text-violet-900' : 'text-gray-500'}`}>
                                  {subquestion.suchAsText[option]}
                                </span>
                              )}
                            </div>
                          </div>

                          {hasOtherInput && (
                          <div
                            className={`transition-all duration-300 ease-in-out overflow-hidden ${
                              shouldShowOtherInput ? 'max-h-60 opacity-100 mt-3' : 'max-h-0 opacity-0'
                            }`}
                            // The box stays in the DOM so it can animate open.
                            // Until the option is ticked it must not be part
                            // of the option's accessible name, or "Other"
                            // is announced as "Other Please tell us more".
                            aria-hidden={shouldShowOtherInput ? undefined : true}
                          >
                            {detailIsRequired && (
                              <label
                                htmlFor={detailFieldName}
                                className="block text-base font-medium text-gray-900 mb-1"
                              >
                                {detailPrompt}
                              </label>
                            )}
                            <textarea
                              id={detailFieldName}
                              placeholder={detailIsRequired ? undefined : detailPrompt}
                              aria-label={detailIsRequired ? undefined : `Details for ${option}`}
                              aria-required={detailIsRequired || undefined}
                              className="font-medium w-full p-2 border rounded-md bg-white text-black min-h-[60px] max-h-[200px] resize-none overflow-y-auto"
                              {...register(detailFieldName)}
                              ref={createTextareaRefCallback(
                                register(detailFieldName).ref
                              )}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              tabIndex={shouldShowOtherInput ? 0 : -1}
                              onInput={(e) => resizeTextarea(e.target)}
                              rows={2}
                            />
                          </div>
                        )}
                        </li>
                      );
                    })}

                    {/* Separator and none options for SelectMultiple */}
                    {subquestion.inputType === "selectMultiple" && hasNoneOptions && (
                      <>
                        <li aria-hidden="true" className="text-center text-gray-700 my-3 font-medium">
                          or
                        </li>

                        {subquestion.noneOptions.map((noneOption) => {
                          const isNoneSelected = Array.isArray(currentValue)
                            ? currentValue?.includes(noneOption)
                            : currentValue === noneOption;

                          const noneSelectHandler = () => {
                            if (isNoneSelected) {
                              setValue(answerKey, []);
                            } else {
                              setValue(answerKey, [noneOption]);
                            }
                            setFormState(prev => ({ ...prev, showOtherInput: false }));
                          };

                          return (
                            <li
                              key={noneOption}
                              role="checkbox"
                              aria-checked={isNoneSelected}
                              tabIndex={0}
                              onClick={noneSelectHandler}
                              onKeyDown={(e) => handleOptionKeyDown(e, noneSelectHandler)}
                              className={`
                                flex flex-col gap-3 p-2 
                                rounded-lg transition-all duration-300 
                                cursor-pointer group
                                ${isNoneSelected
                                  ? 'bg-violet-300 outline outline-2 outline-violet-500 font-semibold'
                                  : 'bg-violet-100 hover:bg-violet-200'
                                }
                              `}
                            >
                              <div className="flex items-center gap-3">
                                <div aria-hidden="true" className={`
                                  flex-shrink-0 w-4 h-4 rounded-full border-2
                                  transition-all duration-200 ease-in-out
                                  ${isNoneSelected
                                    ? 'border-violet-700 bg-violet-700'
                                    : 'border-gray-300 group-hover:border-violet-400'
                                  }
                                  flex items-center justify-center
                                `}>
                                  {isNoneSelected && (
                                    <div className="w-2 h-2 bg-white rounded transition-all duration-200 ease-in-out" />
                                  )}
                                </div>
                                <span className={`font-semibold transition-colors duration-200 ${isNoneSelected ? 'text-violet-900' : 'text-gray-700'}`}>
                                  {noneOption}
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </>
                    )}
                  </ul>
                ) : subquestion.inputType === "freeTextArea" ? (
                  <textarea
                    placeholder={subquestion.placeholder || ""}
                    aria-label={subquestion.subquestionDisplayText}
                    className="w-full p-2 border rounded-md bg-white text-black min-h-[60px] max-h-[200px] resize-none overflow-y-auto"
                    {...register(answerKey)}
                    onInput={(e) => resizeTextarea(e.target)}
                    ref={createTextareaRefCallback(register(answerKey).ref)}
                    rows={2}
                  />
                ) : (
                  <input
                    type={subquestion.inputType === "freeText" ? "text" : subquestion.inputType}
                    placeholder={subquestion.placeholder || ""}
                    aria-label={subquestion.subquestionDisplayText}
                    className="w-full p-2 border rounded-md bg-white text-black"
                    {...register(answerKey)}
                  />
                )}
              </div>                  
              </div>
              
              {/* Horizontal divider between subquestions (except for the last one) */}
              {index < Object.entries(currentQuestion.subquestions).length - 1 && (
                <div className="border-b border-gray-200 my-6"></div>
              )}
            </div>
          );
        })}

{preButtonContent}

<div className={`mt-4 flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4 sm:justify-end`}>

{/* Skip button - only show if hasSkipButton is true */}
{currentQuestion.hasSkipButton && (
  <button
    type="button"
    onClick={() => {
      setFormState(prev => ({
        ...prev,
        isSkipping: true
      }));
      handleNextQuestion({}, true);
    }}
    disabled={hasAnyValue || isSaving}
    className={`w-full sm:w-auto font-semibold flex items-center justify-center py-2 px-4 rounded-md shadow-sm border transition-all duration-300 ease-in-out ${
      hasAnyValue || isSaving
        ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' 
        : 'border-gray-300 hover:bg-white text-violet-800'
    }`}
  >
    {currentQuestion.skipButtonText || "I'm unable to provide this information"}
  </button>
)}

{/* Complete Validation Progress Message */}
{requiresAllSubquestions && !completeValidation.allAnswered && completeValidation.totalCount > 0 && (
  <div className="w-full sm:flex sm:items-center sm:gap-4">
    <div className="flex-grow">
      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <ExclamationIcon className="w-5 h-5 flex-shrink-0 text-amber-700" />
        <span className="font-medium text-sm text-amber-700">
          {completeValidation.totalCount - completeValidation.answeredCount} question{completeValidation.totalCount - completeValidation.answeredCount !== 1 ? 's' : ''} not yet answered. Please scroll up and complete these.
        </span>
      </div>
    </div>
  </div>
)}

{/* Form Navigation Buttons - wrapped in a div to take full width on mobile */}
<div className="w-full sm:w-auto sm:flex-shrink-0">
  <FormActionButtons
    isEditing={formState.isEditing}
    handleCancelEdit={handleCancelEdit}
    hasAnyValue={(requiresAllSubquestions ? completeValidation.allAnswered : hasAnyValue) && !requiredDetailMissing}
    requiresAllSubquestions={requiresAllSubquestions}
    completeValidation={completeValidation}
    allVisibleAnswered={(hasConditionalSqs ? allVisibleAnswered : hasAnyValue) && !requiredDetailMissing}
    isSaving={isSaving}
  />
</div>
</div>          
      </form>
        </>
      )}
    </>
  );
}